import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Check, X, Users, GripVertical, Download } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableCard = ({ s, editingId, editData, setEditData, setEditingId, save, remove, setDeleteTarget }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all" data-testid={`stakeholder-${s.id}`}>
        <CardContent className="p-5">
          {editingId === s.id ? (
            <div className="space-y-2">
              <Input value={editData.name ?? s.name} onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))} className="h-8 text-sm font-semibold" autoFocus data-testid={`stakeholder-edit-name-${s.id}`} />
              <Input value={editData.position ?? s.position} onChange={(e) => setEditData((p) => ({ ...p, position: e.target.value }))} className="h-8 text-xs" placeholder="Position" data-testid={`stakeholder-edit-position-${s.id}`} />
              <div className="flex gap-1">
                <Button size="sm" onClick={() => save(s)} className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`stakeholder-save-${s.id}`}><Check className="w-3 h-3 mr-1" />Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs"><X className="w-3 h-3 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 touch-none" data-testid={`stakeholder-drag-${s.id}`}>
                  <GripVertical className="w-4 h-4" />
                </button>
                <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-teal-700 font-['Outfit']">{s.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                  {s.position && <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-[10px] mt-1 hover:bg-slate-100">{s.position}</Badge>}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditingId(s.id); setEditData({}); }} className="p-1 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600" data-testid={`stakeholder-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setDeleteTarget({ id: s.id, label: s.name })} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500" data-testid={`stakeholder-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const StakeholderSection = ({ title, type, stakeholders, onRefresh, productId }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", position: "" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => { setItems(stakeholders); }, [stakeholders]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const add = async () => {
    if (!formData.name.trim()) return;
    try {
      await axios.post(`${API}/stakeholders`, { ...formData, type, product_id: productId });
      setFormData({ name: "", position: "" });
      setShowForm(false);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  const save = async (s) => {
    try {
      await axios.patch(`${API}/stakeholders/${s.id}`, editData);
      setEditingId(null);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/stakeholders/${id}`); onRefresh(); } catch (e) { console.error(e); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    try {
      await axios.post(`${API}/stakeholders/reorder`, { items: newItems.map((item, idx) => ({ id: item.id, order: idx })) });
    } catch (e) { console.error(e); }
  };

  return (
    <>
    <div className="mb-10" data-testid={`stakeholder-section-${type}`}>
      <h2 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-4">{title}</h2>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-3">
            {items.map((s) => (
              <SortableCard key={s.id} s={s} editingId={editingId} editData={editData} setEditData={setEditData} setEditingId={setEditingId} save={save} remove={remove} setDeleteTarget={setDeleteTarget} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {items.length === 0 && (
        <div className="text-center py-8 text-slate-400"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No {title.toLowerCase()} added yet.</p></div>
      )}

      {showForm ? (
        <Card className="mt-4 border border-slate-200 shadow-sm bg-white" data-testid={`add-${type}-stakeholder-form`}>
          <CardContent className="p-5">
            <div className="flex items-end gap-3">
              <div className="flex-1"><label className="text-xs font-medium text-slate-500 mb-1 block">Name</label>
                <Input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid={`${type}-stakeholder-name-input`} /></div>
              <div className="flex-1"><label className="text-xs font-medium text-slate-500 mb-1 block">Position</label>
                <Input value={formData.position} onChange={(e) => setFormData((p) => ({ ...p, position: e.target.value }))} placeholder="e.g. Director, Manager" data-testid={`${type}-stakeholder-position-input`} /></div>
              <Button onClick={add} disabled={!formData.name.trim()} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid={`${type}-stakeholder-submit-btn`}><Plus className="w-4 h-4 mr-2" />Add</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowForm(true)} className="mt-4 bg-[#0F172A] hover:bg-slate-800 text-white shadow-sm" data-testid={`add-${type}-stakeholder-btn`}><Plus className="w-4 h-4 mr-2" />Add {type === "it" ? "IT" : "Business"} Stakeholder</Button>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => remove(deleteTarget?.id)} title="Delete Stakeholder" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};

export const StakeholdersPanel = () => {
  const { selectedProduct, isAdmin } = useAuth();
  const [allStakeholders, setAllStakeholders] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [globalStakeholders, setGlobalStakeholders] = useState([]);
  const [selectedImports, setSelectedImports] = useState([]);
  const [importing, setImporting] = useState(false);
  const productId = selectedProduct?.id;

  const fetchAll = useCallback(async () => {
    try { 
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/stakeholders`, { params }); 
      setAllStakeholders(r.data); 
    } catch (e) { console.error(e); }
  }, [productId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openImportModal = async () => {
    try {
      const res = await axios.get(`${API}/admin/stakeholders`);
      setGlobalStakeholders(res.data);
      setSelectedImports([]);
      setShowImportModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleImportSelection = (id) => {
    setSelectedImports(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImport = async () => {
    if (!selectedImports.length || !productId) return;
    setImporting(true);
    try {
      await axios.post(`${API}/stakeholders/import-global`, {
        product_id: productId,
        stakeholder_ids: selectedImports,
      });
      setShowImportModal(false);
      fetchAll();
    } catch (e) {
      alert("Error importing stakeholders");
    }
    setImporting(false);
  };

  const business = allStakeholders.filter((s) => s.type !== "it");
  const it = allStakeholders.filter((s) => s.type === "it");

  // Names already in this product to grey them out in the import modal
  const existingNames = new Set(allStakeholders.map(s => s.name));

  return (
    <div data-testid="stakeholders-panel">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Team</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 font-['Outfit']">Stakeholders</h1>
          <p className="text-base text-slate-500 mt-2">Project stakeholders and their roles</p>
        </div>
        {isAdmin && (
          <Button onClick={openImportModal} variant="outline" className="mt-2" data-testid="import-stakeholders-btn">
            <Download className="w-4 h-4 mr-2" /> Import from Directory
          </Button>
        )}
      </div>
      <Separator className="my-8 bg-slate-200" />

      <StakeholderSection title="Business Stakeholders" type="business" stakeholders={business} onRefresh={fetchAll} productId={productId} />
      <Separator className="my-6 bg-slate-200" />
      <StakeholderSection title="IT Stakeholders" type="it" stakeholders={it} onRefresh={fetchAll} productId={productId} />

      {/* Import from Directory Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg" data-testid="import-stakeholders-modal">
          <DialogHeader>
            <DialogTitle>Import Stakeholders from Directory</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-3">Select stakeholders to add to this product. Already-added names are greyed out.</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {globalStakeholders.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No stakeholders in the directory yet. Add them in Admin Panel &rarr; Stakeholders tab.</p>
            ) : (
              globalStakeholders.map(gs => {
                const alreadyExists = existingNames.has(gs.name);
                const isSelected = selectedImports.includes(gs.id);
                return (
                  <div
                    key={gs.id}
                    onClick={() => !alreadyExists && toggleImportSelection(gs.id)}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      alreadyExists ? "bg-slate-50 border-slate-100 opacity-50 cursor-not-allowed" :
                      isSelected ? "bg-[#00D4C8]/10 border-[#00D4C8]" :
                      "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`import-stakeholder-${gs.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                        gs.type === "it" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"
                      }`}>
                        {gs.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{gs.name}</p>
                        <p className="text-xs text-slate-500">
                          {gs.type === "it" ? "IT" : "Business"}{gs.position ? ` · ${gs.position}` : ""}
                          {alreadyExists ? " · Already in project" : ""}
                        </p>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      alreadyExists ? "border-slate-200 bg-slate-100" :
                      isSelected ? "bg-[#00D4C8] border-[#00D4C8]" : "border-slate-300"
                    }`}>
                      {(isSelected || alreadyExists) && (
                        <svg className={`w-3 h-3 ${alreadyExists ? "text-slate-400" : "text-white"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {globalStakeholders.length > 0 && (
            <div className="flex items-center justify-between pt-3 border-t">
              <p className="text-xs text-slate-500">{selectedImports.length} selected</p>
              <Button onClick={handleImport} disabled={!selectedImports.length || importing} data-testid="confirm-import-btn">
                {importing ? "Importing..." : `Import ${selectedImports.length} Stakeholder${selectedImports.length !== 1 ? "s" : ""}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
